import Sortable from "../vendor/sortable.esm"
import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { confirmBackofficeDelete } from "packs/init-backoffice-confirm"
import { initBackofficeUnsavedWarn, markBackofficeFormClean } from "packs/init-backoffice-unsaved-warn"
import { initBackofficeRichText } from "packs/init-backoffice-rich-text"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const syncTitle = (form) => {
  const input = form.querySelector("[data-content-group-title-input]")
  const title = form.closest("[data-faq-section]")?.querySelector("[data-content-group-title]")
  if (!input || !title) return
  const next = (input.value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  if (next) title.textContent = next
}

const visibleFaqItems = (container) =>
  Array.from(container.querySelectorAll("[data-faq-item]"))

const patchReorder = async (url, paramName, ids) => {
  if (!url || ids.length === 0) return true

  const body = new FormData()
  ids.forEach((id) => body.append(`${paramName}[]`, id))
  body.append("_method", "patch")

  const response = await fetch(url, {
    method: "POST",
    body,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrfToken() || "",
    },
    credentials: "same-origin",
  })

  return response.ok
}

const persistItemOrder = async (list) => {
  const ids = visibleFaqItems(list)
    .map((item) => item.dataset.faqItemId)
    .filter(Boolean)
  if (ids.length === 0) return

  const ok = await patchReorder(list.dataset.reorderUrl, "item_ids", ids)
  if (!ok) showBackofficeFlash("Impossible de réordonner les questions", "alert")
}

const persistSectionOrder = async (container) => {
  const ids = Array.from(container.querySelectorAll("[data-faq-section]"))
    .map((section) => section.dataset.faqSectionId)
    .filter(Boolean)
  const ok = await patchReorder(container.dataset.reorderUrl, "section_ids", ids)
  if (!ok) showBackofficeFlash("Impossible de réordonner les sections", "alert")
}

const bindItemsSortable = (list) => {
  if (!list || list.dataset.sortableBound === "true") return
  list.dataset.sortableBound = "true"

  Sortable.create(list, {
    animation: 150,
    handle: "[data-faq-item-handle]",
    draggable: "[data-faq-item]",
    ghostClass: "is-dragging",
    onEnd: () => {
      persistItemOrder(list)
    },
  })
}

const bindSectionsSortable = (container) => {
  if (!container || container.dataset.sortableBound === "true") return
  container.dataset.sortableBound = "true"

  Sortable.create(container, {
    animation: 150,
    handle: "[data-faq-section-handle]",
    draggable: "[data-faq-section]",
    ghostClass: "is-dragging",
    onEnd: () => {
      persistSectionOrder(container)
    },
  })
}

const replaceItemHtml = (itemEl, html) => {
  const wrapper = document.createElement("div")
  wrapper.innerHTML = html.trim()
  const next = wrapper.firstElementChild
  itemEl.replaceWith(next)
  bindItemForm(next.querySelector("form[data-faq-item-form]"))
  initBackofficeRichText(next)
  return next
}

const bindItemForm = (form) => {
  if (!form || form.dataset.faqBound === "true") return
  form.dataset.faqBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const itemEl = form.closest("[data-faq-item]")
    const submits = Array.from(form.querySelectorAll('input[type="submit"], button[type="submit"]'))
    submits.forEach((submit) => {
      submit.disabled = true
    })

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      if (data.html && itemEl) {
        replaceItemHtml(itemEl, data.html)
        initBackofficeUnsavedWarn()
      } else {
        markBackofficeFormClean(form)
      }
      showBackofficeFlash(data.notice || "Question enregistrée.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      submits.forEach((submit) => {
        submit.disabled = false
        submit.removeAttribute("disabled")
      })
    }
  })
}

const bindSectionUi = (sectionEl) => {
  if (!sectionEl) return
  const form = sectionEl.querySelector("form[data-faq-section-form]")
  if (form) bindSectionForm(form)
  sectionEl.querySelectorAll("form[data-faq-item-form]").forEach(bindItemForm)
  bindItemsSortable(sectionEl.querySelector("[data-faq-items]"))
}

const addFaqItem = async (sectionEl) => {
  const url = sectionEl?.dataset.faqItemCreateUrl
  const list = sectionEl?.querySelector("[data-faq-items]")
  if (!url || !list) return

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken() || "",
      },
      credentials: "same-origin",
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showBackofficeFlash(data.error || "Erreur d'ajout", "alert")
      return
    }

    if (data.html) {
      list.insertAdjacentHTML("beforeend", data.html)
      const item = list.querySelector("[data-faq-item]:last-of-type")
      bindItemForm(item?.querySelector("form[data-faq-item-form]"))
      if (item) {
        item.open = true
        initBackofficeRichText(item)
      }
      initBackofficeUnsavedWarn()
    }
    showBackofficeFlash(data.notice || "Question ajoutée.", "notice")
  } catch (_) {
    showBackofficeFlash("Erreur d'ajout", "alert")
  }
}

const bindSectionForm = (form) => {
  if (form.dataset.faqBound === "true") return
  form.dataset.faqBound = "true"

  form.addEventListener("submit", async (event) => {
    event.preventDefault()

    const submits = [
      ...Array.from(form.querySelectorAll('input[type="submit"], button[type="submit"]')),
      ...Array.from(
        document.querySelectorAll(
          `input[type="submit"][form="${form.id}"], button[type="submit"][form="${form.id}"]`
        )
      ),
    ]
    submits.forEach((submit) => {
      submit.disabled = true
    })

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      syncTitle(form)
      markBackofficeFormClean(form)
      showBackofficeFlash(data.notice || "Section enregistrée.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      submits.forEach((submit) => {
        submit.disabled = false
        submit.removeAttribute("disabled")
      })
    }
  })
}

const initBackofficeFaq = () => {
  document.querySelectorAll("[data-faq-section]").forEach(bindSectionUi)
  bindSectionsSortable(document.querySelector("[data-faq-sections]"))

  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.faqUiBound === "true") return
    body.dataset.faqUiBound = "true"

    // Empêche le toggle <details> quand on clique la poignée de drag
    body.addEventListener("click", (event) => {
      if (event.target.closest("[data-faq-section-handle], [data-faq-item-handle]")) {
        event.preventDefault()
      }
    })

    body.addEventListener("click", async (event) => {
      const addItem = event.target.closest("[data-faq-item-add]")
      if (addItem) {
        event.preventDefault()
        addFaqItem(addItem.closest("[data-faq-section]"))
        return
      }

      const removeItem = event.target.closest("[data-faq-item-remove]")
      if (removeItem) {
        event.preventDefault()
        const confirmed = await confirmBackofficeDelete({
          title: "Supprimer cette question ?",
          text: "La question et sa réponse seront retirées définitivement.",
        })
        if (!confirmed) return

        const itemEl = removeItem.closest("[data-faq-item]")
        try {
          const response = await fetch(removeItem.href, {
            method: "DELETE",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
              "X-CSRF-Token": csrfToken() || "",
            },
            credentials: "same-origin",
          })
          const data = await response.json().catch(() => ({}))
          if (!response.ok) {
            showBackofficeFlash(data.error || "Erreur de suppression", "alert")
            return
          }
          itemEl?.remove()
          showBackofficeFlash(data.notice || "Question supprimée.", "notice")
        } catch (_) {
          showBackofficeFlash("Erreur de suppression", "alert")
        }
        return
      }

      const deleteSection = event.target.closest("[data-faq-section-delete]")
      if (deleteSection) {
        event.preventDefault()
        const confirmed = await confirmBackofficeDelete({
          title: "Supprimer cette section ?",
          text: deleteSection.dataset.confirmMessage || "Cette section et toutes ses questions seront définitivement supprimées.",
        })
        if (!confirmed) return

        const sectionEl = deleteSection.closest("[data-faq-section]")
        try {
          const response = await fetch(deleteSection.href, {
            method: "DELETE",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
              "X-CSRF-Token": csrfToken() || "",
            },
            credentials: "same-origin",
          })
          const data = await response.json().catch(() => ({}))
          if (!response.ok) {
            showBackofficeFlash(data.error || "Erreur de suppression", "alert")
            return
          }
          sectionEl?.remove()
          showBackofficeFlash(data.notice || "Section supprimée.", "notice")
        } catch (_) {
          showBackofficeFlash("Erreur de suppression", "alert")
        }
      }
    })

    body.addEventListener("input", (event) => {
      const input = event.target.closest("[data-faq-item-title-input]")
      if (!input) return

      const title = input.closest("[data-faq-item]")?.querySelector("[data-faq-item-title]")
      if (!title) return

      const next = (input.value || "").replace(/\s+/g, " ").trim()
      title.textContent = next || "Nouvelle question"
    })

    body.addEventListener("submit", async (event) => {
      const form = event.target.closest("form[data-faq-section-create]")
      if (!form) return

      event.preventDefault()
      try {
        const response = await fetch(form.action, {
          method: "POST",
          body: new FormData(form),
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrfToken() || "",
          },
          credentials: "same-origin",
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showBackofficeFlash(data.error || "Erreur d'ajout", "alert")
          return
        }

        const container = document.querySelector("[data-faq-sections]")
        if (container && data.html) {
          container.insertAdjacentHTML("beforeend", data.html)
          const created = container.querySelector("[data-faq-section]:last-of-type")
          bindSectionUi(created)
          initBackofficeUnsavedWarn()
        }
        showBackofficeFlash(data.notice || "Section ajoutée.", "notice")
      } catch (_) {
        showBackofficeFlash("Erreur d'ajout", "alert")
      }
    })
  })
}

export { initBackofficeFaq }
